import type { AuthActionResponse, AuthProvider } from '@refinedev/core';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  resetPassword,
  signIn,
  signOut,
  type SignInOutput,
} from 'aws-amplify/auth';
import { getAdmin, getUser } from 'api/resource';
import {
  clearSession,
  getMFAPreference,
  setEmail,
  setSession,
} from 'api/utils/auth';
import { closeWebSocket, registerWebSocket } from 'api/ws/session';
import type { Admin, User } from 'interfaces';
import { useCurrentUserStore } from 'store/UserStore';
import * as Sentry from '@sentry/react';

export const TOKEN_KEY = 'refine-auth';

const LOGIN_ROUTE = '/login';
const USER_PASSWORD_AUTH_FLOW = 'USER_PASSWORD_AUTH';
const TEMPORARY_PASSWORD_CHALLENGE_RESPONSE = 'Temp101!';

type MFAType = 'SMS' | 'TOTP';

type AuthTOTPSetupDetails = {
  sharedSecret: string;
  getSetupUri: (appName: string, accountName?: string) => URL;
};

type PendingSignInStep =
  | {
      type: 'selection';
      allowedMFATypes: MFAType[];
    }
  | {
      type: 'totpSetup';
      details: AuthTOTPSetupDetails;
    }
  | {
      type: 'verification';
      mode: 'sms' | 'totp';
    };

let pendingMFACredentials: { email: string; password: string } | null = null;
let pendingEmail: string | null = null;
let pendingSignInStep: PendingSignInStep | null = null;
let logoutInProgress = false;

const getErrorName = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return String(error.name);
  }
  return 'UnknownAuthError';
};

const captureSanitizedAuthError = (error: unknown, context: string) =>
  Sentry.captureException(new Error(`${context}: ${getErrorName(error)}`));

const signInWithPassword = (email: string, password: string) =>
  signIn({
    username: email,
    password,
    options: { authFlowType: USER_PASSWORD_AUTH_FLOW },
  });

const requiresSMSCredentials = (response: SignInOutput): boolean => {
  const { nextStep } = response;
  if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') return true;

  return (
    nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' &&
    (nextStep.allowedMFATypes?.includes('SMS') ?? false)
  );
};

const getPrimaryRole = (groups: unknown): string => {
  if (Array.isArray(groups) && typeof groups[0] === 'string') {
    return groups[0];
  }
  return '';
};

const fetchCurrentUser = async (
  role: string,
  email: string
): Promise<Admin | User | null> => {
  switch (role) {
    case 'ADMIN': {
      const admin = await getAdmin(email);
      admin.role = null;
      admin.organization = null;
      admin.roleID = '';
      return admin;
    }
    case 'USER':
    case 'PROVIDER':
      return getUser(email);
    default:
      return null;
  }
};

const requireLogin = () => {
  closeWebSocket();
  return {
    authenticated: false,
    redirectTo: LOGIN_ROUTE,
  };
};

export const getPendingSignInStep = () => pendingSignInStep;

export const clearPendingSignIn = () => {
  pendingMFACredentials = null;
  pendingEmail = null;
  pendingSignInStep = null;
};

const handleSignInResponse = async (
  response: SignInOutput,
  email: string
): Promise<AuthActionResponse> => {
  const { nextStep } = response;
  if (!nextStep) {
    captureSanitizedAuthError(
      new Error('Missing Cognito next step'),
      'Sign in failed'
    );
    return { success: false, type: 'unsupportedSignInStep' };
  }

  switch (nextStep.signInStep) {
    case 'DONE':
      setEmail(email);
      clearPendingSignIn();
      return { success: true, type: 'onSuccess' };
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
      pendingSignInStep = { type: 'verification', mode: 'sms' };
      return { success: true, type: 'confirmSignInWithSMSCode' };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      pendingMFACredentials = null;
      pendingSignInStep = { type: 'verification', mode: 'totp' };
      return { success: true, type: 'confirmSignInWithTOTPCode' };
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
      const allowedMFATypes = nextStep.allowedMFATypes ?? [];
      if (!allowedMFATypes.length) {
        captureSanitizedAuthError(
          new Error('No MFA methods returned'),
          'MFA selection failed'
        );
        return { success: false, type: 'unsupportedSignInStep' };
      }
      pendingSignInStep = { type: 'selection', allowedMFATypes };
      return { success: true, type: 'continueSignInWithMFASelection' };
    }
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      pendingMFACredentials = null;
      pendingSignInStep = {
        type: 'totpSetup',
        details: nextStep.totpSetupDetails,
      };
      return { success: true, type: 'continueSignInWithTOTPSetup' };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      await confirmSignIn({
        challengeResponse: TEMPORARY_PASSWORD_CHALLENGE_RESPONSE,
      });
      await resetPassword({ username: email });
      return { success: true, type: 'newPasswordRequired' };
    default:
      captureSanitizedAuthError(
        new Error(`Unsupported step: ${nextStep.signInStep}`),
        'Sign in failed'
      );
      return { success: false, type: 'unsupportedSignInStep' };
  }
};

export const continueSignInWithMFASelection = async (
  mfaType: MFAType
): Promise<AuthActionResponse> => {
  if (
    pendingSignInStep?.type !== 'selection' ||
    !pendingSignInStep.allowedMFATypes.includes(mfaType)
  ) {
    return { success: false, type: 'onError' };
  }

  try {
    const response = await confirmSignIn({ challengeResponse: mfaType });
    return handleSignInResponse(response, pendingEmail ?? '');
  } catch (error) {
    captureSanitizedAuthError(error, 'MFA selection failed');
    return { success: false, type: 'onError' };
  }
};

export const resendMfaCode = async (): Promise<AuthActionResponse> => {
  try {
    if (!pendingMFACredentials) {
      return { success: false, type: 'onError' };
    }
    const { email, password } = pendingMFACredentials;
    const response = await signInWithPassword(email, password);
    if (response.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      pendingMFACredentials = null;
    }
    return { success: true, type: 'resendMfaCode' };
  } catch (error) {
    captureSanitizedAuthError(error, 'SMS MFA resend failed');
    return { success: false, type: 'onError' };
  }
};

export const authProvider: AuthProvider = {
  login: async ({
    email,
    password,
    code,
    remember,
  }: {
    email?: string;
    password?: string;
    code?: string;
    remember?: boolean;
  }): Promise<AuthActionResponse> => {
    try {
      let response: SignInOutput;
      if (code) {
        const normalizedCode = code.replace(/[\s-]/g, '');
        response = await confirmSignIn({ challengeResponse: normalizedCode });
      } else if (!email || !password) {
        return { success: false, type: 'onError' };
      } else {
        clearPendingSignIn();
        await signOut();
        if (remember) {
          localStorage.setItem('email', email);
        } else {
          localStorage.removeItem('email');
        }
        pendingEmail = email;
        response = await signInWithPassword(email, password);
        pendingMFACredentials = requiresSMSCredentials(response)
          ? { email, password }
          : null;
      }
      return handleSignInResponse(response, email ?? pendingEmail ?? '');
    } catch (error) {
      switch (getErrorName(error)) {
        case 'NotAuthorizedException':
          if (code) {
            return {
              success: false,
              type: 'signInSessionExpired',
              redirectTo: LOGIN_ROUTE,
            };
          }
          return {
            success: false,
            type: 'onError',
            redirectTo: LOGIN_ROUTE,
          };
        case 'UserNotFoundException':
          return {
            success: false,
            type: 'onError',
            redirectTo: LOGIN_ROUTE,
          };
        case 'CodeMismatchException':
          return {
            success: false,
            type: 'onError',
            redirectTo: '/verify',
          };
        case 'LimitExceededException':
          return {
            success: false,
            type: 'LimitExceededException',
            redirectTo: LOGIN_ROUTE,
          };
        case 'SignInException':
          captureSanitizedAuthError(error, 'Sign in failed');
          return {
            success: false,
            type: 'signInSessionExpired',
            redirectTo: LOGIN_ROUTE,
          };
        default:
          captureSanitizedAuthError(error, 'Sign in failed');
      }
    }
    return { success: false, type: 'onError', redirectTo: LOGIN_ROUTE };
  },
  logout: async () => {
    logoutInProgress = true;
    closeWebSocket();
    try {
      await signOut({ global: true });
    } finally {
      clearPendingSignIn();
      clearSession();
      logoutInProgress = false;
    }
    return {
      success: true,
      redirectTo: LOGIN_ROUTE,
    };
  },
  check: async () => {
    if (logoutInProgress) {
      return requireLogin();
    }

    try {
      const session = await fetchAuthSession();
      const { idToken } = session.tokens ?? {};
      if (!idToken) return requireLogin();

      const { payload } = idToken;
      const email = payload.email;
      if (typeof email !== 'string') return requireLogin();

      const primaryRole = getPrimaryRole(payload['cognito:groups']);
      const { preferred, enabled } = await fetchMFAPreference();
      setSession({
        email,
        role: primaryRole,
        mfaPreference: preferred || getMFAPreference(),
        mfaEnabled: Boolean(enabled?.length),
      });

      const user = await fetchCurrentUser(primaryRole, email);
      if (!user) {
        captureSanitizedAuthError(
          new Error(`Unsupported role: ${primaryRole}`),
          'Session check failed'
        );
        return requireLogin();
      }

      useCurrentUserStore.setState(user);
      Sentry.setUser({ email: user.email, id: user.id });
      if (user.cognitoID) {
        await registerWebSocket(
          user.cognitoID,
          user.organizationID || undefined
        );
      }

      return {
        authenticated: true,
        redirectTo: '/',
      };
    } catch (error) {
      Sentry.captureException(error);
      return requireLogin();
    }
  },
  getPermissions: async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const { idToken } = session.tokens ?? {};
      const primaryRole = getPrimaryRole(idToken?.payload['cognito:groups']);
      switch (primaryRole) {
        case 'ADMIN':
          return 'ADMIN';
        case 'USER':
          return 'USER';
        case 'PROVIDER':
          return 'PROVIDER';
        default:
          return 'USER';
      }
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  },
  register: async () => null,
  onError: async (error) => {
    Sentry.captureException(error);
    return { error };
  },
  updatePassword: async ({ email, code, newPassword }) => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      return {
        type: 'onSuccess',
        success: true,
      };
    } catch (error) {
      Sentry.captureException(error);
      if (getErrorName(error) === 'LimitExceededException') {
        return {
          type: 'LimitExceededException',
          success: false,
        };
      }
      return {
        type: 'onFailure',
        success: false,
      };
    }
  },
  forgotPassword: async ({ email }) => {
    const { nextStep } = await resetPassword({ username: email });
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        return {
          type: 'resetPassword',
          success: true,
        };
      case 'DONE':
        return {
          type: 'onSuccess',
          success: true,
        };
      default:
        return {
          type: 'onFailure',
          success: false,
        };
    }
  },
};
